import type { ReactNode } from 'react';
import { CheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';

export interface Step {
  id: string;
  label: ReactNode;
  description?: ReactNode;
}

/** Numbered steps in heavy figures, joined by hairlines. `current` is the 0-based active index. */
export function Stepper({
  steps,
  current,
  className,
  orientation = 'horizontal',
}: {
  steps: Step[];
  current: number;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}) {
  const vertical = orientation === 'vertical';
  return (
    <ol className={cn(vertical ? 'grid gap-0' : 'flex items-start', className)}>
      {steps.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        return (
          <li
            key={s.id}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn(
              'relative flex gap-3',
              vertical ? 'pb-6 last:pb-0' : 'flex-1 flex-col pr-3 last:flex-none last:pr-0',
            )}
          >
            <div className={cn('flex items-center', vertical ? 'flex-col' : 'gap-3')}>
              <span
                className={cn(
                  'num relative z-10 inline-flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-extrabold transition-colors duration-300',
                  state === 'done' && 'border-primary bg-primary text-on-primary',
                  state === 'current' &&
                    'border-primary bg-surface text-primary-ink shadow-[0_0_0_4px_var(--gh-primary-soft)]',
                  state === 'todo' && 'border-hairline-strong bg-surface text-ink-subtle',
                )}
              >
                {state === 'done' ? <CheckIcon weight="bold" className="size-3.5" /> : i + 1}
              </span>
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    vertical ? 'absolute left-3.5 top-7 bottom-0 w-px' : 'h-px flex-1',
                    i < current ? 'bg-primary' : 'bg-hairline-strong',
                  )}
                />
              )}
            </div>
            <div className={cn('min-w-0', !vertical && 'pr-2')}>
              <div
                className={cn(
                  'text-sm font-semibold',
                  state === 'todo' ? 'text-ink-muted' : 'text-ink',
                )}
              >
                {s.label}
              </div>
              {s.description && (
                <div className="mt-0.5 text-xs text-ink-muted">{s.description}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
